/* =====================================================================
 * QueuedNotice — 실행 슬롯 대기 중 표시
 *
 * 대기 중과 실행 중이 똑같은 점 3개(`DotsSpinner`)로 보이던 문제를 닫는다.
 * 사용자가 알아야 하는 건 "멈춘 게 아니라 순서를 기다리는 중"이고, 서버가
 * 순번을 알려주므로 그걸 그대로 보여준다.
 *
 * 두 가지 대기가 있다:
 *   stage='job' — 백엔드 실행 큐. 순번이 의미를 가진다.
 *   stage='llm' — AI 쪽 vLLM 호출 게이트. 이미 실행 중이며 모델 슬롯만
 *                 기다리는 상태라 순번이 없다.
 * ===================================================================== */

import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { Clock } from 'lucide-react';
import type { QueuedState } from '@auw/workspace-utils';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;
  animation: ${pulse} 2s ease-in-out infinite;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    padding: 5px 10px;
  }
`;

const Icon = styled(Clock)`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const Detail = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
`;

export interface QueuedNoticeProps {
  queued: QueuedState;
}

export const QueuedNotice: React.FC<QueuedNoticeProps> = ({ queued }) => {
  const { t } = useTranslation();

  if (queued.stage === 'llm') {
    return (
      <Wrapper role="status" aria-live="polite">
        <Icon aria-hidden />
        <span>{t('common:chat.queuedModel')}</span>
      </Wrapper>
    );
  }

  // `position` 은 1 = 바로 다음 차례. 서버가 순번을 못 준 경우(0)에는
  // 숫자를 지어내지 않고 일반 문구만 보여준다.
  const ahead = queued.position > 1 ? queued.position - 1 : 0;

  return (
    <Wrapper role="status" aria-live="polite">
      <Icon aria-hidden />
      <span>{t('common:chat.queuedWaiting')}</span>
      {ahead > 0 && <Detail>· {t('common:chat.queuedAhead', { count: ahead })}</Detail>}
      {queued.position === 1 && <Detail>· {t('common:chat.queuedNext')}</Detail>}
      {queued.etaSec != null && queued.etaSec > 0 && (
        <Detail>· {t('common:chat.queuedEta', { seconds: queued.etaSec })}</Detail>
      )}
    </Wrapper>
  );
};
